import { AddressResponse } from '@App/interfaces/applicant.response';
import { getStreetName } from '@Shared/helpers/getStreetName';

import { AddressValue } from '../types';

export const isValidPostCode = (postcode: string) => {
    const finalPostcode = postcode.replace(/\s/g, '').replace(/-/g, '');
    const regex = /^[A-Z]{1,2}[0-9|A-Z]{1,2} ?[0-9][A-Z]{2}$/i;
    return regex.test(finalPostcode);
};

export const normalizePostCode = (value: any) => {
    let finalValue = (value + '')
        .replace(/\s/g, '')
        .replace(/,/g, '')
        .replace(/-/g, '');
    if (finalValue.length > 3) {
        finalValue = [
            finalValue.slice(0, finalValue.length - 3),
            ' ',
            finalValue.slice(-3),
        ].join('');
    }
    return finalValue;
};

export const getResidenceInfo = (values?: AddressValue | null) => {
    if (!values) return null;

    const unitName = values.subBuildingName || values.departmentName || null;

    const buildingName =
        values.buildingName ||
        values.organisationName ||
        (values.poBox ? `PO Box ${values.poBox}` : null) ||
        null;

    const items = [unitName, buildingName].filter(Boolean);
    return items.length ? items.join(', ') : null;
};

export const getStreetInfo = (values?: AddressValue | null) => {
    if (!values) return null;

    const { buildingNumber, dependantThoroughfare, streetName } = values;

    const street =
        dependantThoroughfare && streetName
            ? `${dependantThoroughfare} ${streetName}`
            : streetName || dependantThoroughfare || null;

    const items = [buildingNumber, street].filter(Boolean);
    return items.length ? items.join(' ') : null;
};

export const getAddressLine1 = (address: AddressValue) => {
    const residenceInfo = getResidenceInfo(address);
    const streetInfo = getStreetInfo(address);

    const items = [residenceInfo, streetInfo].filter(Boolean);
    return items.length ? items.join(', ') : '';
};

const joinItems = (
    items: Array<string | null | undefined>,
    separator = ', ',
) => {
    const filtered = items.filter(Boolean);
    return filtered.length ? filtered.join(separator) : null;
};

export const getAddressLine1FromResponse = (address: AddressResponse) => {
    const residenceInfo = joinItems([
        address.subBuildingName,
        address.buildingName,
    ]);
    const streetInfo = joinItems(
        [address.buildingNumber, getStreetName(address.addressLine1)],
        ' ',
    );

    const line1 = joinItems([residenceInfo, streetInfo]);
    return line1 || '';
};

const normalizeComparableValue = (value?: string | null) =>
    value?.trim().toLowerCase() || '';

const findMatchingAddress = (
    addresses: AddressValue[] | null | undefined,
    current?: AddressValue | null,
) => {
    if (!addresses?.length || !current) return undefined;

    return addresses.find(
        (address) =>
            normalizeComparableValue(address.subBuildingName) ===
                normalizeComparableValue(current.subBuildingName) &&
            normalizeComparableValue(address.buildingName) ===
                normalizeComparableValue(current.buildingName) &&
            normalizeComparableValue(address.buildingNumber) ===
                normalizeComparableValue(current.buildingNumber) &&
            normalizeComparableValue(address.streetName) ===
                normalizeComparableValue(current.streetName) &&
            normalizeComparableValue(address.city) ===
                normalizeComparableValue(current.city),
    );
};

export const getMatchedAddress = (
    addresses: AddressValue[] | null | undefined,
    current?: AddressValue | null,
): AddressValue | null | undefined => {
    if (!current) return current;

    const matchedAddress = findMatchingAddress(addresses, current);

    if (matchedAddress) {
        return {
            ...matchedAddress,
            line1: getAddressLine1(matchedAddress),
            line2: '',
        };
    }

    const currentAddress = {
        subBuildingName: current.subBuildingName,
        buildingName: current.buildingName,
        buildingNumber: current.buildingNumber,
        streetName: current.streetName,
        city: current.city,
        country: current.country,
        postcode: current.postcode,
        county: current.county,
    };

    return {
        ...currentAddress,
        line1: getAddressLine1(currentAddress),
        line2: '',
        uprn: '',
        udprn: '',
    };
};
